import React, { useEffect, useRef, useState } from 'react';
import styled, { createGlobalStyle, keyframes } from 'styled-components';
import Lottie from "lottie-react";
import octopusAnimation from "../assets/Octopus.json";
import twoImg from "../assets/two.jpg";

const translations = {
  en: {
    header: {
      logo: "drOcto",
    },
  },
};

const GlobalStyle = createGlobalStyle`
  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  body {
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
    line-height: 1.6;
    color: #1a1a1a;
    overflow-x: hidden;
  }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-20px); }
`;

const floatCard = keyframes`
  0%, 100% { transform: translateY(0px) rotate(0deg); }
  50% { transform: translateY(-15px) rotate(1deg); }
`;

const fillBar = keyframes`
  from { width: 0; }
`;

const pulse = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(30, 144, 255, 0.4); }
  50% { box-shadow: 0 0 20px 10px rgba(30, 144, 255, 0.6); }
`;

const Header = styled.header`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 20;
  background: #ffffff;
  padding: 1rem;
  border-bottom: 1px solid rgba(30, 144, 255, 0.2);
`;

const HeaderContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 1200px;
  margin: 0 auto;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const LogoContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const LogoText = styled.span`
  font-size: clamp(1.25rem, 5vw, 1.5rem);
  font-weight: 800;
  letter-spacing: -0.02em;
  color: #1a1a1a;
  text-align: center;
`;

const Hero = styled.section`
  background: #ffffff;
  min-height: 100vh;
  display: flex;
  align-items: center;
  position: relative;
  overflow: hidden;
  padding-top: 100px;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="grid" width="10" height="10" patternUnits="userSpaceOnUse"><path d="M 10 0 L 0 0 0 10" fill="none" stroke="rgba(30, 144, 255, 0.2)" stroke-width="0.5"/></pattern></defs><rect width="100" height="100" fill="url(%23grid)"/></svg>');
    animation: ${float} 20s ease-in-out infinite;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  position: relative;
  z-index: 2;
`;

const HeroContent = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 60px;
  align-items: center;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 40px;
  }
`;

const HeroText = styled.div`
  h1 {
    font-size: 3.5rem;
    font-weight: 700;
    color: #1a1a1a;
    margin-bottom: 20px;
    line-height: 1.2;

    @media (max-width: 768px) {
      font-size: 2.5rem;
    }
  }

  p {
    font-size: 1.3rem;
    color: #4a5568;
    margin-bottom: 30px;
  }
`;

const CtaButton = styled.a`
  background: transparent;
  border: 2px solid #1E90FF;
  color: #1E90FF;
  padding: 15px 40px;
  border-radius: 50px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  text-decoration: none;
  display: inline-block;
  box-shadow: 0 10px 30px rgba(30, 144, 255, 0.3);
  transition: all 0.3s ease;

  &:hover {
    background: #1E90FF;
    color: #ffffff;
    transform: translateY(-3px);
    box-shadow: 0 15px 40px rgba(30, 144, 255, 0.4);
  }
`;

const HeroVisual = styled.div`
  position: relative;
`;

const FloatingCard = styled.div`
  background: #ffffff;
  border-radius: 20px;
  padding: 30px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.1);
  animation: ${floatCard} 6s ease-in-out infinite;
`;

const SkillMeter = styled.div`
  margin: 15px 0;
`;

const SkillLabel = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
  font-weight: 600;
  color: #1a1a1a;
`;

const SkillBar = styled.div`
  height: 8px;
  background: rgba(240, 240, 240, 0.8);
  border-radius: 4px;
  overflow: hidden;
`;

const SkillProgress = styled.div`
  height: 100%;
  border-radius: 4px;
  animation: ${fillBar} 2s ease-out;

  &.critical-thinking {
    background: #1E90FF;
    width: 88%;
  }

  &.situational-awareness {
    background: #28A745;
    width: 92%;
  }

  &.analytical-reasoning {
    background: #FF6B6B;
    width: 85%;
  }
`;

const ProblemSection = styled.section`
  padding: 100px 0;
  background: rgba(240, 248, 255, 0.9);
`;

const SectionTitle = styled.h2`
  font-size: 2.8rem;
  font-weight: 700;
  text-align: center;
  margin-bottom: 20px;
  color: #1a1a1a;

  @media (max-width: 768px) {
    font-size: 2.2rem;
  }
`;

const ProblemGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 40px;
  margin-top: 60px;
`;

const ProblemCard = styled.div`
  background: #ffffff;
  padding: 40px;
  border-radius: 15px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease;
  border-left: 5px solid #1E90FF;

  &:hover {
    transform: translateY(-5px);
  }
`;

const SolutionSection = styled.section`
  padding: 100px 0;
  background: #ffffff;
`;

const SolutionFeatures = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 40px;
  margin-top: 60px;
`;

const FeatureCard = styled.div`
  text-align: center;
  padding: 40px 20px;
  border-radius: 15px;
  background: rgba(30, 144, 255, 0.1);
  transition: all 0.3s ease;

  &:hover {
    transform: scale(1.05);
    background: rgba(30, 144, 255, 0.2);
  }
`;

const FeatureIcon = styled.div`
  width: 80px;
  height: 80px;
  background: #1E90FF;
  border-radius: 50%;
  margin: 0 auto 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  color: #ffffff;
`;

const VisualSection = styled.section`
  padding: 100px 0;
  background: #ffffff;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
`;

const ResponsiveImage = styled.img`
  width: 100%;
  max-width: 800px;
  height: auto;
  border-radius: 15px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  object-fit: cover;
  display: block;
  margin: 0 auto;

  @media (max-width: 768px) {
    max-width: 100%;
  }
`;

const SkillDevelopmentSection = styled.section`
  padding: 100px 0;
  background: #ffffff;
`;

const SkillGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 30px;
  margin-bottom: 40px;
`;

const SkillCard = styled.div`
  background: ${({ selected, skill }) =>
    selected
      ? skill === 'critical-thinking'
        ? '#1E90FF'
        : skill === 'analytical-reasoning'
        ? '#FF6B6B'
        : '#28A745'
      : '#ffffff'};
  color: ${props => (props.selected ? '#ffffff' : '#1a1a1a')};
  padding: 30px;
  border-radius: 15px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  border-left: 5px solid ${({ skill }) =>
    skill === 'critical-thinking'
      ? '#1E90FF'
      : skill === 'analytical-reasoning'
      ? '#FF6B6B'
      : '#28A745'};
  transition: all 0.3s ease;
  cursor: pointer;
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 15px;

  &:hover {
    transform: translateY(-5px);
    background: ${({ selected, skill }) =>
      selected
        ? skill === 'critical-thinking'
          ? '#1E90FF'
          : skill === 'analytical-reasoning'
          ? '#FF6B6B'
          : '#28A745'
        : 'rgba(30, 144, 255, 0.1)'};
  }

  h3 {
    font-size: 1.5rem;
    margin-bottom: 10px;
    color: ${props => (props.selected ? '#ffffff' : '#1a1a1a')};
  }

  p {
    font-size: 1rem;
    color: ${props => (props.selected ? '#e6f0ff' : '#4a5568')};
    flex-grow: 1;
  }
`;

const SkillIcon = styled.div`
  width: 60px;
  height: 60px;
  background: ${props => (props.selected ? '#ffffff' : props.color)};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  color: ${props => (props.selected ? props.color : '#ffffff')};
  margin-bottom: 15px;
`;

const SelectIndicator = styled.div`
  position: absolute;
  top: 20px;
  right: 20px;
  font-size: 1.2rem;
  color: ${props => (props.selected ? '#ffffff' : '#1E90FF')};
`;

const PricingDisplay = styled.div`
  text-align: center;
  margin: 40px 0;
  padding: 20px;
  border-radius: 15px;
  background: ${props => (props.isComplete ? 'rgba(30, 144, 255, 0.1)' : '#ffffff')};
  animation: ${props => (props.isComplete ? pulse : 'none')} 2s infinite;

  h3 {
    font-size: 2rem;
    font-weight: 700;
    color: #1E90FF;
    margin-bottom: 10px;
  }

  p {
    font-size: 1.2rem;
    color: #4a5568;
  }

  .complete-message {
    font-size: 1.1rem;
    color: #1E90FF;
    font-weight: 600;
    margin-top: 10px;
  }
`;

const Footer = styled.footer`
  background: #1a1a1a;
  color: #ffffff;
  padding: 60px 0 30px;
  text-align: center;
`;

const StatsCounter = styled.span`
  display: inline-block;
  font-size: 2rem;
  font-weight: 700;
  color: #1E90FF;
`;

const FinalSection = styled.section`
  padding: 100px 0;
  background: rgba(240, 248, 255, 0.9);
`;

const FinalCtaButton = styled(CtaButton)`
  font-size: 1.2rem;
  padding: 20px 50px;
`;

const App = () => {
  const counterRef = useRef(null);
  const [selectedSkills, setSelectedSkills] = useState([]);

  const handleSkillToggle = (skill) => {
    setSelectedSkills((prev) =>
      prev.includes(skill)
        ? prev.filter((s) => s !== skill)
        : [...prev, skill]
    );
  };

  const calculatePrice = () => {
    const count = selectedSkills.length;
    if (count === 0) return { amount: 0, text: "Please select at least one skill to get started." };
    if (count === 1) return { amount: 299, text: "₹299/month for 1 skill" };
    if (count === 2) return { amount: 399, text: "₹399/month for 2 skills" };
    return { amount: 499, text: "₹499/month for all 3 skills" };
  };

  const priceInfo = calculatePrice();
  const isCompletePlan = selectedSkills.length === 3;

  useEffect(() => {
    const animateCounter = (element, target) => {
      let current = 0;
      const increment = target / 100;
      const timer = setInterval(() => {
        current += increment;
        element.textContent = Math.floor(current).toLocaleString() + '+';
        if (current >= target) {
          element.textContent = target.toLocaleString() + '+';
          clearInterval(timer);
        }
      }, 20);
    };

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const counter = entry.target.querySelector('.stats-counter');
            if (counter && !counter.dataset.animated) {
              counter.dataset.animated = 'true';
              animateCounter(counter, 8000000000);
            }
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll('.problem-card').forEach((card) => observer.observe(card));

    const handleSmoothScroll = (e) => {
      e.preventDefault();
      const targetId = e.currentTarget.getAttribute('href').substring(1);
      const targetElement = document.getElementById(targetId);
      if (targetElement) {
        targetElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    };

    document.querySelectorAll('a[href^="#"]').forEach((anchor) => {
      anchor.addEventListener('click', handleSmoothScroll);
    });

    return () => {
      document.querySelectorAll('a[href^="#"]').forEach((anchor) => {
        anchor.removeEventListener('click', handleSmoothScroll);
      });
      observer.disconnect();
    };
  }, []);

  return (
    <>
      <GlobalStyle />
      <Header>
        <HeaderContent>
          <LogoContainer>
            <Lottie animationData={octopusAnimation} loop={true} style={{ width: 60, height: 60 }} />
            <LogoText>{translations.en.header.logo}</LogoText>
          </LogoContainer>
        </HeaderContent>
      </Header>
      <Hero>
        <Container>
          <HeroContent>
            <HeroText>
              <h1><strong>Building Tomorrow's Workforce Today</strong></h1>
              <p>
                In a world of 8 billion people, grades don't matter anymore. What matters is your{' '}
                <strong>Relevancy Score</strong> - the AI-powered measure of critical thinking, situational
                awareness, and analytical reasoning.
              </p>
              <CtaButton href="/test"><strong>Start Test</strong></CtaButton>
            </HeroText>
            <HeroVisual>
              <FloatingCard>
                <h3><strong>Daily Progress</strong></h3>
                <SkillMeter>
                  <SkillLabel>
                    <span><strong>Critical Thinking</strong></span>
                    <span><strong>88%</strong></span>
                  </SkillLabel>
                  <SkillBar>
                    <SkillProgress className="critical-thinking" />
                  </SkillBar>
                </SkillMeter>
                <SkillMeter>
                  <SkillLabel>
                    <span><strong>Situational Awareness</strong></span>
                    <span><strong>92%</strong></span>
                  </SkillLabel>
                  <SkillBar>
                    <SkillProgress className="situational-awareness" />
                  </SkillBar>
                </SkillMeter>
                <SkillMeter>
                  <span style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontWeight: 600 }}>
                    <span><strong>Analytical Reasoning</strong></span>
                    <span><strong>85%</strong></span>
                  </span>
                  <SkillBar>
                    <SkillProgress className="analytical-reasoning" />
                  </SkillBar>
                </SkillMeter>
                <p style={{ marginTop: '20px', color: '#1E90FF', fontWeight: 600 }}>
                  <strong>Challenge completed: School Event Planning</strong>
                </p>
              </FloatingCard>
            </HeroVisual>
          </HeroContent>
        </Container>
      </Hero>
      <ProblemSection>
        <Container>
          <SectionTitle><strong>The Problem We're Solving</strong></SectionTitle>
          <p style={{ textAlign: 'center', fontSize: '1.2rem', marginBottom: '40px', color: '#4a5568' }}>
            <strong>Growing population + Same degrees = Unemployable graduates</strong>
          </p>
          <ProblemGrid>
            <ProblemCard className="problem-card">
              <h3><strong>📈 Population Crisis</strong></h3>
              <p>
                With <StatsCounter className="stats-counter" ref={counterRef}><strong>8,000,000,000+</strong></StatsCounter> people on Earth
                and growing daily, competition for jobs has never been fiercer.
              </p>
            </ProblemCard>
            <ProblemCard className="problem-card">
              <h3><strong>📜 Outdated Metrics</strong></h3>
              <p>
                Companies no longer care about grades or degrees. They need employees who can think
                critically and solve real problems.
              </p>
            </ProblemCard>
            <ProblemCard className="problem-card">
              <h3><strong>🏔️ Remote Area Gap</strong></h3>
              <p>
                Kids from remote areas lack access to tools that help them develop and showcase their true
                potential to employers.
              </p>
            </ProblemCard>
          </ProblemGrid>
        </Container>
      </ProblemSection>
      <SolutionSection id="solution">
        <Container>
          <SectionTitle><strong>The RelevanceIQ Solution</strong></SectionTitle>
          <p style={{ textAlign: 'center', fontSize: '1.2rem', marginBottom: '40px', color: '#4a5568' }}>
            <strong>AI-powered daily skill building that creates a comprehensive relevancy profile</strong>
          </p>
          <SolutionFeatures>
            <FeatureCard>
              <FeatureIcon>🧠</FeatureIcon>
              <h3><strong>Daily AI Interaction</strong></h3>
              <p>Engage with our specialized AI chatbot designed specifically for the future workforce</p>
            </FeatureCard>
            <FeatureCard>
              <FeatureIcon>🎯</FeatureIcon>
              <h3><strong>Real-World Challenges</strong></h3>
              <p>Solve age-appropriate problems that mirror actual workplace scenarios</p>
            </FeatureCard>
            <FeatureCard>
              <FeatureIcon>📊</FeatureIcon>
              <h3><strong>Progress Tracking</strong></h3>
              <p>Build a comprehensive profile that shows consistent growth over time</p>
            </FeatureCard>
            <FeatureCard>
              <FeatureIcon>🏢</FeatureIcon>
              <h3><strong>Employer Access</strong></h3>
              <p>Companies can see your true potential beyond grades and degrees</p>
            </FeatureCard>
          </SolutionFeatures>
        </Container>
      </SolutionSection>
      <VisualSection>
        <Container>
          <ResponsiveImage src={twoImg} alt="Empowering Future Workforce" />
        </Container>
      </VisualSection>
      <SkillDevelopmentSection>
        <Container>
          <SectionTitle>Unlock Your Child's Potential</SectionTitle>
          <p style={{ textAlign: 'center', fontSize: '1.2rem', marginBottom: '40px', color: '#4a5568' }}>
            <strong>Click on the skills below to select or deselect them and customize your child's growth plan.</strong>
          </p>
          <SkillGrid>
            <SkillCard
              selected={selectedSkills.includes('critical-thinking')}
              skill="critical-thinking"
              onClick={() => handleSkillToggle('critical-thinking')}
            >
              <SkillIcon selected={selectedSkills.includes('critical-thinking')} color="#1E90FF">🧠</SkillIcon>
              <h3>Critical Thinking</h3>
              <p>
                Critical thinking helps your child solve problems by looking at them from different angles, asking questions, and making smart decisions. It’s like teaching them to think like a detective!
              </p>
              <SelectIndicator selected={selectedSkills.includes('critical-thinking')}>
                {selectedSkills.includes('critical-thinking') ? '✓ Selected' : 'Select'}
              </SelectIndicator>
            </SkillCard>
            <SkillCard
              selected={selectedSkills.includes('analytical-reasoning')}
              skill="analytical-reasoning"
              onClick={() => handleSkillToggle('analytical-reasoning')}
            >
              <SkillIcon selected={selectedSkills.includes('analytical-reasoning')} color="#FF6B6B">🧩</SkillIcon>
              <h3>Analytical Reasoning</h3>
              <p>
                Analytical reasoning is about breaking down information, finding patterns, and understanding how things work. It’s like solving a puzzle to make sense of the world.
              </p>
              <SelectIndicator selected={selectedSkills.includes('analytical-reasoning')}>
                {selectedSkills.includes('analytical-reasoning') ? '✓ Selected' : 'Select'}
              </SelectIndicator>
            </SkillCard>
            <SkillCard
              selected={selectedSkills.includes('situational-awareness')}
              skill="situational-awareness"
              onClick={() => handleSkillToggle('situational-awareness')}
            >
              <SkillIcon selected={selectedSkills.includes('situational-awareness')} color="#28A745">👀</SkillIcon>
              <h3>Situational Awareness</h3>
              <p>
                Situational awareness teaches your child to notice what’s happening around them, understand the situation, and act wisely. It’s like being a superhero who knows what’s going on and how to respond.
              </p>
              <SelectIndicator selected={selectedSkills.includes('situational-awareness')}>
                {selectedSkills.includes('situational-awareness') ? '✓ Selected' : 'Select'}
              </SelectIndicator>
            </SkillCard>
          </SkillGrid>
          <PricingDisplay isComplete={isCompletePlan}>
            <h3>{priceInfo.amount > 0 ? `₹${priceInfo.amount}/month` : 'No Skills Selected'}</h3>
            <p>{priceInfo.text}</p>
            {isCompletePlan && (
              <p className="complete-message">Complete Plan Unlocked! Best Value!</p>
            )}
          </PricingDisplay>
          <CtaButton href="/signup">Start Enhancing Skills</CtaButton>
        </Container>
      </SkillDevelopmentSection>
      <FinalSection>
        <Container>
          <SectionTitle><strong>Ready to Build Your Relevancy Score?</strong></SectionTitle>
          <p style={{ fontSize: '1.2rem', marginBottom: '40px', color: '#4a5568' }}>
            <strong>Join thousands of students who are already preparing for the jobs of tomorrow!</strong>
          </p>
          <FinalCtaButton href="/test"><strong>Start Test</strong></FinalCtaButton>
        </Container>
      </FinalSection>
      <Footer>
        <Container>
          <p style={{ marginTop: '30px', color: '#4a5568' }}><strong>© 2025</strong> drOcto. All rights reserved.</p>
        </Container>
      </Footer>
    </>
  );
};

export default App;